import { fetchTeamsForCustomer } from 'actions/action-lookups';
import CreateIcon from '@mui/icons-material/Create';
import React from 'react';
import { ApplicationManagerClient } from 'clients/application-manager-client';
import { BooleanOption, NumberOption } from 'interfaces/forms/types';
import { Button, Grid, Popover } from '@mui/material';
import { CustomerId } from 'interfaces/RecordTypes';
import { notifyError, notifySuccess } from 'actions/action-notifications';
import { RolesEnum } from 'constants/enums';
import { SearchField } from 'containers/application-manager/components/search-field';
import { useDebounce } from 'use-debounce/lib';
import { useDispatch } from 'react-redux';
import { UserPermissions } from 'interfaces/user/Permissions';
import { UserUtils } from 'utils/user-utils';
import { useTypedSelector } from 'hooks/use-typed-selector';
import { buildQaId } from 'utils/build-qa-id';

import { IGetServiceGroupsResponseItem } from 'models/application-manager/users/IGetServiceGroupsResponse';

import { TeamModal } from './modals/team-modal';
import { BaseTable } from '../../base-table/base-table';
import { FieldTypes } from '../../types';
import { FormField } from '../../popover-field-forms';
import {
  ICustomerRoleChild,
  IGetUsersResponse,
  IGetUsersResponseItem,
  IGetUsersTableParent,
  IUserResponseRoleItem,
} from '../types';
import CustomerDropdown from '../../components/customer-dropdown';

import { IHeaderCell } from '../../base-table/types';
import { IPopoverStateBase } from '../../ar/types';
import { logger } from '../../../../winston-logger';
import { SectionTitleStyled } from '../../components/section-title';
import { styles } from '../index.styles';
import { USER_TEAM_FILTER_TYPES } from '../../../../constants/index';
import BlueBadgeIcon from '../../../../lib/application-manager/users/blue-badge.svg';

const initialSearchFieldValue = '';
const searchFieldPlaceholder = 'Search by team name';

type IPopoverStateBaseObjectType = Partial<IGetUsersResponseItem> &
  Partial<IUserResponseRoleItem> & {
    customerRoles?: any[];
    permissions?: any[];
    serviceGroups?: any[];
  };

type IPopoverState = IPopoverStateBase<IPopoverStateBaseObjectType>;

const initialPopoverState = {
  open: false,
} as IPopoverState;

const getQaId = buildQaId('application-manager.users.team-manager', '.');

export const Teams: React.FC = (): JSX.Element => {
  const classes: any = styles();

  const dispatch = useDispatch();

  const allUserTeams = useTypedSelector(state => state.lookups.lookupUserTeams);

  // #region component state
  const [selectedRoles, setSelectedRoles] = React.useState<NumberOption[]>([]);
  const [selectedSites, setSelectedSites] = React.useState<NumberOption[]>([]);
  const [selectedLicensedStates, setSelectedLicensedStates] = React.useState<NumberOption[]>([]);
  const [selectedStatus, setSelectedStatus] = React.useState<BooleanOption>();
  const [editingTeamId, setEditingTeamId] = React.useState<undefined | number>(undefined);
  const [addModalOpen, setAddModalOpen] = React.useState<boolean>(false);
  const [searchTerm, setSearchTerm] = React.useState<string>(initialSearchFieldValue);
  const [debouncedSearchTerm] = useDebounce(searchTerm, 500);
  const [userTeams, setUserTeams] = React.useState<{ results: any[]; totalCount: number }>({
    results: allUserTeams,
    totalCount: allUserTeams ? allUserTeams.length : 0,
  });

  const [serviceGroupsPerCustomer, setServiceGroupsPerCustomer] = React.useState<
    NumberOption[] | undefined
  >(undefined);

  const [clinicOptions, setClinicOptions] = React.useState<NumberOption[] | undefined>();
  const customerClinicOptions = clinicOptions && clinicOptions.length > 0 ? clinicOptions : [];
  const [permissionOptions, setPermissionOptions] = React.useState<NumberOption[] | undefined>();

  const stateSelectedCustomerId = useTypedSelector(state => state.filters.selectedCustomerId);

  const [selectedCustomerId, setSelectedCustomerId] =
    React.useState<number>(stateSelectedCustomerId);

  const serviceGroupsForCustomer = serviceGroupsPerCustomer || [];

  const [reloadTrigger, setReloadTrigger] = React.useState<Date | undefined>(undefined);

  const [popoverState, setPopoverState] = React.useState<IPopoverState>(initialPopoverState);
  // #endregion

  // #region redux state

  const customers = useTypedSelector(state => state.filters.customers);
  const lookups = useTypedSelector(state => state.lookups);
  const roles = useTypedSelector(state => state.lookups.roles);
  const user = useTypedSelector(state => state.auth.currentUser);
  // #endregion

  // #region effects

  const loadServiceGroupsAndClinics = () => {
    (async () => {
      // Load permissions
      try {
        const permissions = await ApplicationManagerClient.getPermissionsAssignablePerCustomer();
        setPermissionOptions(
          permissions.data.map(x => {
            return { label: x.display_name, value: x.id };
          }),
        );
      } catch (error) {
        logger.error(error);
        dispatch(notifyError('Error fetching permissions'));
      }

      // Load service groups
      try {
        // Tech debt: we should load all at once for all sites.
        const result = new Promise<{
          customerId: CustomerId;
          serviceGroups: IGetServiceGroupsResponseItem[];
        }>(async resolve => {
          const apiResponse = await ApplicationManagerClient.getCustomerServiceGroups(
            selectedCustomerId,
          );
          resolve({
            customerId: selectedCustomerId,
            serviceGroups: apiResponse.data,
          });
        });

        const serviceGroupPromise = await Promise.resolve(result);

        const serviceGroups: NumberOption[] =
          serviceGroupPromise &&
          serviceGroupPromise.serviceGroups &&
          serviceGroupPromise.serviceGroups.length
            ? serviceGroupPromise.serviceGroups.map(s => ({
                label: s.display_name,
                value: s.id,
              }))
            : [];

        setServiceGroupsPerCustomer(serviceGroups);
      } catch (error) {
        logger.error(error);
        dispatch(notifyError('Error fetching service groups'));
      }

      // Load clinics
      try {
        const results = await ApplicationManagerClient.getAllCustomerClinics(selectedCustomerId);

        setClinicOptions(
          results.data
            .sort((a, b) => a.name.localeCompare(b.name))
            .map(x => {
              return { label: x.name, value: x.id };
            }),
        );
      } catch (error) {
        logger.error(error);
        dispatch(notifyError('Error fetching clinics'));
      }
    })();
  };

  React.useEffect(() => {
    dispatch(fetchTeamsForCustomer(selectedCustomerId));
    loadServiceGroupsAndClinics();
  }, []);

  React.useEffect(() => {
    dispatch(fetchTeamsForCustomer(selectedCustomerId));
    loadServiceGroupsAndClinics();
  }, [selectedCustomerId]);

  React.useEffect(() => {
    let results: any[] = [];
    let totalCount = 0;
    if (allUserTeams && allUserTeams.length > 0) {
      results = allUserTeams;
      // results = [...results, ...allSmsPredefinedMessages];
      totalCount = allUserTeams.length;
    }
    // todo: filters

    if (debouncedSearchTerm) {
      const lowercaseSearchTerm = `${debouncedSearchTerm}`.toLowerCase();
      const serviceGroupNames = serviceGroupsForCustomer.map(s => ({ name: s.label, id: s.value }));
      const clinicNames = customerClinicOptions.map(c => ({ name: c.label, id: c.value }));
      results = results.filter(r => {
        const rowFilters = r.filters || [];
        const rowName = `${r.teamName}`.toLowerCase();
        const serviceGroupsForFilters = serviceGroupNames
          .filter(s =>
            rowFilters.some(
              (r: any) =>
                r.resource_id === s.id && r.resource_type === USER_TEAM_FILTER_TYPES.SERVICE_GROUP,
            ),
          )
          .map(s => `${s.name}`.toLowerCase());
        const clinicsForFilters = clinicNames
          .filter(s =>
            rowFilters.some(
              (r: any) =>
                r.resource_id === s.id && r.resource_type === USER_TEAM_FILTER_TYPES.CLINIC,
            ),
          )
          .map(s => `${s.name}`.toLowerCase());

        return (
          clinicsForFilters.some(c => c.includes(lowercaseSearchTerm)) ||
          serviceGroupsForFilters.some(s => s.includes(lowercaseSearchTerm)) ||
          rowName.includes(lowercaseSearchTerm)
        );
      });
    }

    setUserTeams({ results: results, totalCount: totalCount });
    loadServiceGroupsAndClinics();
    setReloadTrigger(new Date());
  }, [searchTerm, lookups.lookupUserTeams, selectedSites, selectedCustomerId, debouncedSearchTerm]);

  // #region constants

  const COLUMN_SETTINGS: IHeaderCell<any, any>[] = [
    {
      label: 'Team Name',
      sortable: true,
      parentKey: 'teamName',
      parentValueFormatter: (value: unknown) => {
        if (value) {
          return `${value}`;
        }
        return '-';
      },
      showParentTooltip: cellContent => cellContent != null && cellContent.length > 25,
    },
    {
      label: 'Service Groups & Clinics',
      sortable: true,
      parentKey: 'filters',
      parentValueFormatter: (value: unknown) => {
        let retStrArr: any[] = [];

        if (Array.isArray(value) && value.length > 0) {
          for (const v of value) {
            const filterResourceId = v.resource_id;
            if (
              v.resource_type === USER_TEAM_FILTER_TYPES.CLINIC &&
              customerClinicOptions &&
              customerClinicOptions?.find(c => c.value === filterResourceId)
            ) {
              retStrArr = [
                ...retStrArr,
                `${customerClinicOptions?.find(c => c.value === filterResourceId)?.label}`,
              ];
            }
            if (
              v.resource_type === USER_TEAM_FILTER_TYPES.SERVICE_GROUP &&
              serviceGroupsForCustomer &&
              serviceGroupsForCustomer.find(c => c.value === filterResourceId)
            ) {
              retStrArr = [
                ...retStrArr,
                `${serviceGroupsForCustomer.find(c => c.value === filterResourceId)?.label}`,
              ];
            }
          }

          if (retStrArr.length > 0) {
            return retStrArr.join(', ');
          }
        }
        return '-';
      },
      parentCellClick: (event, parent) => {
        setEditingTeamId(parent.teamId);
      },
    },
  ];
  // #endregion

  // #endregion

  // #region memos
  const userPermittedToAddTeams = React.useMemo<boolean>(() => {
    return UserUtils.userIsPermitted(UserPermissions.ApplicationManagerUsersManageUserLevel);
  }, [user]);

  const columnSettings = React.useMemo(() => {
    if (userPermittedToAddTeams) {
      return COLUMN_SETTINGS.concat({
        sortable: true,
        parentComponent: parent => {
          return (
            <>
              <CreateIcon
                data-qa-id={getQaId('button.edit-team')}
                onClick={e => {
                  e.stopPropagation();
                  setEditingTeamId(parent.teamId);
                }}
              />
            </>
          );
        },
        childComponent: (parent, child) => {
          const role = roles.find(x => x.id === child.roleId);

          if (role && role.fallback_role_id) {
            return (
              <img
                className={classes.pointer}
                src={BlueBadgeIcon}
                onClick={e => {
                  e.stopPropagation();
                }}
              />
            );
          }
          return undefined;
        },
      });
    }
    return COLUMN_SETTINGS;
  }, [roles, userPermittedToAddTeams, customers, serviceGroupsForCustomer, customerClinicOptions]);

  const siteOptions = React.useMemo<NumberOption[]>(() => {
    return customers
      .map(x => {
        return { label: x.name, value: x.id };
      })
      .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
  }, [customers]);

  const forcedQueryParams = React.useMemo<{}>(() => {
    return {
      customerIds: selectedSites?.map(x => x.value.toString()),
      roleIds: selectedRoles?.map(x => x.value.toString()),
      stateIds: selectedLicensedStates?.map(x => x.value.toString()),
      active: selectedStatus?.value.toString() || '',
    };
  }, [selectedSites, selectedRoles, selectedLicensedStates, selectedStatus]);
  // #endregion

  // #region helper functions

  const afterRequestSuccess = (callbacks: (() => void)[] | undefined = undefined) => {
    dispatch(notifySuccess('Saved'));
    if (callbacks) {
      callbacks.forEach(cb => cb());
    }
  };

  const onSubmitFieldUpdate = async (value: unknown) => {
    try {
      let fieldToUpdate = popoverState.field;
      let valueToUpdate = value;

      // If updating role, SGs, or permissions individually, we need to build up the
      // customerRoles array in form of [{customer_id, role_id, permissionIds, serviceGroupIds}]
      if (
        [FieldTypes.RoleId, FieldTypes.Permissions, FieldTypes.ServiceGroups].includes(
          popoverState.field as FieldTypes,
        )
      ) {
        const customerRole: any = {
          customer_id: popoverState.parent.customerId,
        };
        if (popoverState.field === FieldTypes.RoleId) {
          customerRole.role_id = value;
        }

        if (popoverState.field === FieldTypes.Permissions) {
          customerRole.role_id = popoverState.parent.roleId;
          customerRole.permissionIds = value;
        }

        if (popoverState.field === FieldTypes.ServiceGroups) {
          customerRole.role_id = popoverState.parent.roleId;
          customerRole.serviceGroupIds = value;
        }
        valueToUpdate = [customerRole];
        fieldToUpdate = 'customerRoles';
      }

      const request = ApplicationManagerClient.updateUserField(
        popoverState.parent.userId,
        fieldToUpdate,
        valueToUpdate,
      );
      await request;

      afterRequestSuccess([() => setPopoverState(initialPopoverState)]);
      setReloadTrigger(new Date());
    } catch (error) {
      logger.error(error);
      dispatch(notifyError('An error occurred'));
    } finally {
      setPopoverState(initialPopoverState);
    }
  };

  const renderPopover = (): JSX.Element => {
    const handleCancel = () => setPopoverState(initialPopoverState);
    const handleSubmit = (value: unknown) => onSubmitFieldUpdate(value);

    return (
      <Popover
        id="form"
        open={popoverState.open}
        anchorEl={popoverState?.anchorElement}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transitionDuration={{ exit: 0 }}
        onClose={() => setPopoverState(initialPopoverState)}
      >
        <FormField
          data-qa-id={`${popoverState.field}-${popoverState.parent?.userId || ''}`}
          initialValue={popoverState.value}
          handleCancel={handleCancel}
          handleSubmit={handleSubmit}
          fieldType={popoverState.fieldType}
          {...popoverState}
        />
      </Popover>
    );
  };

  const openPopover = (
    element: HTMLElement,
    value: unknown,
    config: {
      parent: IGetUsersResponseItem | IUserResponseRoleItem;
      fieldType: FieldTypes;
      field: any;
      forceAsParent?: boolean;
      customerId?: number;
    },
  ): void => {
    if (config.parent === null) {
      throw new Error('Cannot select a parent cell.');
    }
    setPopoverState({
      open: true,
      anchorElement: element,
      parent: config.parent,
      field: config.field,
      fieldType: config.fieldType,
      value: value,
      forceAsParent: config.forceAsParent,
      customerId: config.customerId,
    });
  };

  const renderNestedTable = (parent: any) => {
    const setRecordId = (c: ICustomerRoleChild) => `${c.customerId}_${c.roleId}`;
    type IParentRow = IUserResponseRoleItem & { children: any[] | null; id: string };
    const childrenRecords = parent.children.map((child: IUserResponseRoleItem) => ({
      ...child,
      id: setRecordId(child),
      userId: parent.id,
      children: [],
    }));
    return (
      <BaseTable<
        {
          results: IParentRow[];
          totalCount: number;
        },
        IParentRow,
        IUserResponseRoleItem
      >
        searchTerm={JSON.stringify({
          type: 'MULTIPLE',
          search: [
            {
              values: searchTerm,
              columns: ['teamName'],
              fullMatch: false,
              active: true,
            },
            {
              values: searchTerm,
              columns: ['filters'],
              fullMatch: false,
              active: true,
            },
          ],
        })}
        actionsPermitted
        enableParentCheckboxes={false}
        enableChildCheckboxes={false}
        dataSet={{ totalCount: childrenRecords.length, results: childrenRecords }}
        columnSettings={[
          {
            label: 'Role',
            sortable: false,
            parentKey: 'roleId',
            childColSpan: 1,
            parentCellValueOverride: (record: IUserResponseRoleItem) => {
              return roles.find(x => x.id === record.roleId)?.display_name || '';
            },
            parentCellClick: (event, record: IUserResponseRoleItem) => {
              if (
                // These roles are not able to edit
                ![RolesEnum.PharmacistCustomer, RolesEnum.LiaisonCustomer].includes(record.roleId)
              ) {
                openPopover(
                  event.currentTarget,
                  roles.find(x => x.id === record.roleId)?.id ?? [],
                  {
                    parent: record,
                    field: 'roleId',
                    fieldType: FieldTypes.RoleId,
                  },
                );
              }
            },
          },
          {
            label: 'Sites',
            sortable: false,
            parentKey: 'customerId',
            childColSpan: 1,
            parentCellValueOverride: (record: IUserResponseRoleItem) => {
              return customers.find(x => x.id === record.customerId)?.name || '';
            },
          },
          {
            label: 'Service Groups',
            sortable: false,
            parentKey: 'serviceGroups',
            childColSpan: 1,
            parentCellValueOverride: (record: IUserResponseRoleItem) => {
              return (
                record.serviceGroups
                  ?.map(({ name }) => name)
                  .sort()
                  .join(', ') || ''
              );
            },
            parentCellClick: (event, record: IUserResponseRoleItem) => {
              openPopover(event.currentTarget, record.serviceGroups?.map(({ id }) => id) ?? [], {
                parent: record,
                field: 'serviceGroups',
                fieldType: FieldTypes.ServiceGroups,
                customerId: record.customerId,
              });
            },
          },
          {
            label: 'Permissions',
            sortable: false,
            parentKey: 'permissions',
            childColSpan: 1,
            parentCellValueOverride: (record: IUserResponseRoleItem) => {
              return (
                record.permissions
                  ?.map(({ name }) => name)
                  .sort()
                  .join(', ') || ''
              );
            },
            parentCellClick: (event, record: IUserResponseRoleItem) => {
              openPopover(event.currentTarget, record.permissions?.map(({ id }) => id) ?? [], {
                parent: record,
                field: 'permissions',
                fieldType: FieldTypes.Permissions,
              });
            },
          },
        ]}
        paginationQueryParamSettings={{
          pageNumberQueryStringKey: 'pageNumber',
          pageSizeQueryStringKey: 'pageSize',
          searchTermQueryStringKey: 'searchTerm',
          sortOrderQueryStringKey: 'sortOrder',
          sortPropQueryStringKey: 'sortProp',
        }}
        childPkSelector={c => setRecordId(c)}
        parentPkSelector={p => p.id}
        classesOverride={{
          lastCell: userPermittedToAddTeams ? classes.lastCell : undefined,
        }}
        triggerReload={reloadTrigger}
      />
    );
  };

  // #endregion

  return (
    <>
      {renderPopover()}
      {editingTeamId != null && (
        <TeamModal
          open={editingTeamId != null}
          editingTeam={{ teamId: editingTeamId }}
          onCancel={() => {
            setEditingTeamId(undefined);
          }}
          selectedCustomerId={selectedCustomerId}
          onSuccess={response => {
            setEditingTeamId(undefined);
            setReloadTrigger(new Date());
            dispatch(fetchTeamsForCustomer(selectedCustomerId));
          }}
        />
      )}

      {userPermittedToAddTeams && (
        <TeamModal
          open={addModalOpen}
          onCancel={() => setAddModalOpen(false)}
          selectedCustomerId={selectedCustomerId}
          onSuccess={() => {
            setAddModalOpen(false);
            setReloadTrigger(new Date());
            dispatch(fetchTeamsForCustomer(selectedCustomerId));
          }}
          editingTeam={undefined}
        />
      )}
      <SectionTitleStyled title="Team Management" />
      <Grid container direction="column">
        {/* Top Part */}
        <Grid item>
          <Grid container justifyContent="space-between" alignItems="flex-end">
            <Grid container item xs={8} spacing={3}>
              <Grid item xs={3}>
                {/* <ReactSelect
                  label="Site"
                  name="Site"
                  value={selectedSites}
                  fields={siteOptions}
                  data-qa-id={getQaId('filter.site')}
                  handleOnChange={(value: NumberOption[] | undefined) => {
                    setSelectedSites(value || []);
                  }}
                /> */}
                <CustomerDropdown
                  isLabelOn
                  selectedCustomerId={selectedCustomerId}
                  setSelectedCustomerId={customerId => setSelectedCustomerId(customerId)}
                  requiredPermission={UserPermissions.SignatureRequirementManage}
                />
              </Grid>

              <Grid item xs={5}>
                <div className={classes.searchFieldTeam}>
                  <SearchField
                    width={48}
                    value={searchTerm}
                    onChange={setSearchTerm}
                    initialValue={initialSearchFieldValue}
                    placeholder={searchFieldPlaceholder}
                  />
                </div>
              </Grid>
            </Grid>
            {userPermittedToAddTeams && (
              <Grid item xs={3}>
                <Grid container justifyContent="flex-end">
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => setAddModalOpen(true)}
                    data-qa-id={getQaId('button.add-team')}
                  >
                    Add Team
                  </Button>
                </Grid>
              </Grid>
            )}
          </Grid>
        </Grid>
        {/* Bottom Part */}
        <Grid item>
          <BaseTable<IGetUsersResponse, IGetUsersTableParent, IUserResponseRoleItem>
            searchTerm={JSON.stringify({
              type: 'MULTIPLE',
              search: [
                {
                  values: debouncedSearchTerm,
                  columns: ['teamName'],
                  fullMatch: false,
                  active: false,
                },
                // {
                //   values: debouncedSearchTerm,
                //   columns: ['filters'],
                //   fullMatch: false,
                //   active: debouncedSearchTerm.length > 0,
                // },
              ],
            })}
            actionsPermitted={userPermittedToAddTeams}
            enableParentCheckboxes={false}
            enableChildCheckboxes={false}
            dataSet={userTeams}
            columnSettings={columnSettings}
            paginationQueryParamSettings={{
              pageNumberQueryStringKey: 'pageNumber',
              pageSizeQueryStringKey: 'pageSize',
              searchTermQueryStringKey: 'searchTerm',
              sortOrderQueryStringKey: 'sortOrder',
              sortPropQueryStringKey: 'sortProp',
            }}
            childPkSelector={c => c.roleId}
            parentPkSelector={p => p.id}
            classesOverride={{
              lastCell: userPermittedToAddTeams ? classes.lastCell : undefined,
            }}
            triggerReload={reloadTrigger}
            forcedQueryParams={forcedQueryParams}
            customChildrenComponent={parent => renderNestedTable(parent)}
            transformResponseData={(data: IGetUsersResponse) => {
              return {
                ...data,
                results: data.results.map(({ roles: responseItemRoles, ...rest }) => ({
                  ...rest,
                  userId: rest.id,
                  children: responseItemRoles,
                })),
              } as IGetUsersResponse;
            }}
          />
        </Grid>
      </Grid>
    </>
  );
};
